import React, { useState, useEffect } from 'react';
import { Box, Typography, Grid, Card, CardContent, Button, Chip, CardMedia, Alert, Skeleton } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { useToast } from '../../context/ToastContext';
import AssignmentIcon from '@mui/icons-material/Assignment';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import LockIcon from '@mui/icons-material/Lock';
import SatisfactionSurvey from '../../components/learner/SatisfactionSurvey';

interface CategorySurvey {
    _id: string;
    title: string;
    description?: string;
    imageUrl?: string;
    completed: boolean;
    surveyed?: boolean;
}

const LearnerSurveys = () => {
    const [pendingSurveys, setPendingSurveys] = useState<CategorySurvey[]>([]);
    const [completedSurveys, setCompletedSurveys] = useState<CategorySurvey[]>([]);
    const [loading, setLoading] = useState(true);
    const [selectedCategory, setSelectedCategory] = useState<CategorySurvey | null>(null);
    const { showToast } = useToast();
    const navigate = useNavigate();

    useEffect(() => {
        fetchSurveys();
    }, []);

    const fetchSurveys = async () => {
        try {
            const res = await api.get('/api/satisfaction/pending');
            setPendingSurveys(res.data.pending || []);
            setCompletedSurveys(res.data.completed || []);
        } catch (err) {
            console.error("Error fetching surveys", err);
            showToast("Erreur lors du chargement des enquêtes", "error");
        } finally {
            setLoading(false);
        }
    };

    const handleSurveyComplete = () => {
        setSelectedCategory(null);
        fetchSurveys(); // Refresh the list
    };

    if (selectedCategory) {
        return (
            <Box sx={{ maxWidth: 900, mx: 'auto', p: 4 }}>
                <Button
                    onClick={() => setSelectedCategory(null)}
                    sx={{ mb: 3, fontFamily: 'Quicksand, sans-serif' }}
                >
                    ← Retour aux enquêtes
                </Button>
                <SatisfactionSurvey
                    categoryId={selectedCategory._id}
                    categoryName={selectedCategory.title}
                    onComplete={handleSurveyComplete}
                />
            </Box>
        );
    }

    return (
        <Box sx={{ p: 4 }}>
            <Typography variant="h4" gutterBottom sx={{ fontFamily: 'Quicksand, sans-serif', fontWeight: 700, mb: 1 }}>
                Enquêtes de satisfaction
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ fontFamily: 'Quicksand, sans-serif', mb: 4 }}>
                Complétez les enquêtes pour les catégories que vous avez terminées
            </Typography>

            {loading ? (
                <Grid container spacing={3}>
                    {[1, 2, 3].map(i => (
                        <Grid key={i} size={{ xs: 12, md: 6, lg: 4 }}>
                            <Skeleton variant="rectangular" height={200} sx={{ borderRadius: 2 }} />
                        </Grid>
                    ))}
                </Grid>
            ) : (
                <>
                    {/* Pending Surveys */}
                    {pendingSurveys.length > 0 && (
                        <>
                            <Typography variant="h6" sx={{ fontFamily: 'Quicksand, sans-serif', fontWeight: 600, mb: 2 }}>
                                En attente ({pendingSurveys.length})
                            </Typography>
                            <Grid container spacing={3} sx={{ mb: 4 }}>
                                {pendingSurveys.map(survey => (
                                    <Grid key={survey._id} size={{ xs: 12, md: 6, lg: 4 }}>
                                        <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column', borderRadius: 3, border: '2px solid #fbbf24' }}>
                                            {survey.imageUrl && (
                                                <CardMedia
                                                    component="img"
                                                    height="140"
                                                    image={survey.imageUrl}
                                                    alt={survey.title}
                                                />
                                            )}
                                            <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                                    <AssignmentIcon color="warning" />
                                                    <Chip label="À compléter" color="warning" size="small" />
                                                </Box>
                                                <Typography variant="h6" sx={{ fontFamily: 'Quicksand, sans-serif', fontWeight: 600, mb: 1 }}>
                                                    {survey.title}
                                                </Typography>
                                                <Button
                                                    variant="contained"
                                                    color="warning"
                                                    onClick={() => setSelectedCategory(survey)}
                                                    sx={{ mt: 'auto', fontFamily: 'Quicksand, sans-serif', textTransform: 'none', fontWeight: 600 }}
                                                >
                                                    Répondre à l'enquête
                                                </Button>
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                ))}
                            </Grid>
                        </>
                    )}

                    {/* Completed Surveys */}
                    {completedSurveys.length > 0 && (
                        <>
                            <Typography variant="h6" sx={{ fontFamily: 'Quicksand, sans-serif', fontWeight: 600, mb: 2 }}>
                                Complétées ({completedSurveys.length})
                            </Typography>
                            <Grid container spacing={3}>
                                {completedSurveys.map(survey => (
                                    <Grid key={survey._id} size={{ xs: 12, md: 6, lg: 4 }}>
                                        <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column', borderRadius: 3, opacity: 0.8 }}>
                                            {survey.imageUrl && (
                                                <CardMedia
                                                    component="img"
                                                    height="140"
                                                    image={survey.imageUrl}
                                                    alt={survey.title}
                                                />
                                            )}
                                            <CardContent sx={{ flexGrow: 1 }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                                    <CheckCircleIcon color="success" />
                                                    <Chip label="Complétée" color="success" size="small" />
                                                </Box>
                                                <Typography variant="h6" sx={{ fontFamily: 'Quicksand, sans-serif', fontWeight: 600, mb: 1 }}>
                                                    {survey.title}
                                                </Typography>
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                ))}
                            </Grid>
                        </>
                    )}

                    {pendingSurveys.length === 0 && completedSurveys.length === 0 && (
                        <Alert severity="info" sx={{ fontFamily: 'Quicksand, sans-serif' }}>
                            Aucune enquête disponible pour le moment. Terminez des catégories pour débloquer les enquêtes de satisfaction.
                        </Alert>
                    )}
                </>
            )}
        </Box>
    );
};

export default LearnerSurveys;
